import React, { useRef } from 'react';
import {
  Col,
  Form,
  FormItemProps,
  Row,
  Typography,
  AutoComplete,
  AutoCompleteProps,
  Tooltip,
} from 'antd';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import classNames from 'classnames';

type Props<T extends FieldValues> = {
  autoCompleteProps?: AutoCompleteProps;
  controller: UseControllerProps<T>;
  errorCol?: 12 | 24;
  onFocus?: () => void;
  onBlur?: () => void;
  onClear?: () => void;
  newRegex?: RegExp;
  showErrorTooltip?: boolean;
} & Omit<FormItemProps, 'children'>;

function AutoSuggestField<T extends FieldValues>(props: Props<T>) {
  const {
    autoCompleteProps,
    controller,
    errorCol = 24,
    label,
    onBlur,
    onFocus,
    onClear,
    newRegex,
    showErrorTooltip = false,
    ...rest
  } = props;
  const { fieldState, field } = useController<T>(controller);
  const { error } = fieldState;
  const inputRef = useRef<any>(null);

  const onChangeInput = (value) => {
    // handle max text length input suggest
    if (
      autoCompleteProps?.maxTagTextLength &&
      value &&
      value.length > autoCompleteProps?.maxTagTextLength
    ) {
      return;
    }
    if (newRegex) {
      if ((newRegex && newRegex.test(value)) || value === '') {
        field.onChange(value);
      } else {
        !field.value && field.onChange('');
      }
    } else {
      field.onChange(value);
    }
  };

  const handleClearInput = () => {
    onClear && onClear();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const {
    className: autoCompleteClassName,
    style: autoCompleteStyle,
    ...restAutoCompleteProps
  } = autoCompleteProps || {};

  const showTooltip = showErrorTooltip && !!error;
  const inputClassName = classNames(autoCompleteClassName, {
    'form-tooltip-error': showTooltip,
  });

  const autoCompleteNode = (
    <AutoComplete
      {...restAutoCompleteProps}
      {...field}
      ref={inputRef}
      className={inputClassName}
      onChange={onChangeInput}
      onFocus={() => {
        onFocus && onFocus();
      }}
      onBlur={() => {
        onBlur && onBlur();
      }}
      onClear={handleClearInput}
      style={{ width: '100%', ...autoCompleteStyle }}
    />
  );

  const fieldContent = showTooltip ? (
    <Tooltip
      placement="topLeft"
      title={error?.message}
      overlayClassName="form-tooltip-error-tooltip"
    >
      <span className="form-tooltip-wrapper">{autoCompleteNode}</span>
    </Tooltip>
  ) : (
    autoCompleteNode
  );

  return (
    <Form.Item
      colon={false}
      label={
        <Typography.Text
          style={{
            width: '100%',
          }}
        >
          {label}
        </Typography.Text>
      }
      labelAlign="left"
      labelCol={label ? { span: 24 } : undefined}
      wrapperCol={label ? { span: 24 } : undefined}
      validateStatus={error ? 'error' : ''}
      {...rest}
    >
      <Row gutter={4}>
        <Col span={errorCol}>
          <Row gutter={8}>
            <Col span={24}>{fieldContent}</Col>
          </Row>
        </Col>
        {!showTooltip && (
          <Col span={errorCol}>
            {error && (
              <Typography.Text
                className="ant-form-item-explain ant-form-item-explain-error"
                type="danger"
                ellipsis={{ tooltip: true }}
                style={{ lineHeight: errorCol === 24 ? '16px' : '42px' }}
              >
                {error.message}
              </Typography.Text>
            )}
          </Col>
        )}
      </Row>
    </Form.Item>
  );
}

export default AutoSuggestField;
